import { Box, Button, Card, CardBody, Heading, Stack, Tab, Tabs } from "grommet";
import { RouletteItems } from "../RouletteItems";
import RouletteItemSettingsForm from "./form/RouletteItemSettingsForm";
import RouletteSettingsForm from "./form/RouletteSettingsForm";
import { useRouletteStyleContext } from "./context/RouletteStyleContextProvider";
import { createContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateRouletteCategoryMutation, useDeleteRouletteCategoryMutation, useGetRouletteQuery, usePartialUpdateRouletteCategoryMutation, useUpdateRouletteCategoryMutation } from "../../../api/rouletteWidgetApi";
import { rouletteItemBgType } from "../RouletteConstants";
import { mergeProps } from "../../../utils/mergeUtils";
import { RouletteThemesTab } from "./tab/RouletteThemesTab";
import RouletteComponent from "../RouletteComponent";

export const RouletteSettingsFormContext = createContext();

const RouletteDesignSettings = () => {

    const { t, ready } = useTranslation('roulette', { useSuspense: false });
    
    const rouletteRef = useRef();
    const [settingsFormItem, setSettingsFormItem] = useState();
    const [createCategoryForm, setCreateCategoryForm] = useState(false);
    const [isSuccessfullyUpdatedCategory, setSuccessfullyUpdatedCategory] = useState();
    const [isSuccessfullyCreatedCategory, setSuccessfullyCreatedCategory] = useState();
    const [isSuccessfullyDeletedCategory, setSuccessfullyDeletedCategory] = useState();

    const { rouletteStyle } = useRouletteStyleContext();

    const { data: rouletteData, isLoading: isRouletteDataLoading, isSuccess: isRouletteDataLoaded } = useGetRouletteQuery();
    const [updateCategory, { isLoading: isCategoryUpdating }] = usePartialUpdateRouletteCategoryMutation();
    const [updateRouletteCategory, { isLoading: isUpdatingCategory, isSuccess: isCategoryUpdated }] = useUpdateRouletteCategoryMutation();
    const [createRouletteCategory, { isLoading: isCreatingCategory, isSuccess: isCategoryCreated }] = useCreateRouletteCategoryMutation();
    const [deleteRouletteCategory, { isLoading: isDelitingCategory, isSuccess: isCategoryDeleted }] = useDeleteRouletteCategoryMutation();

    const rouletteBackground = useMemo(() => {

        const data = mergeProps(rouletteData, rouletteStyle);

        if (!data) return {};
        
        if (data.backgroundType === rouletteItemBgType.COLOR) {
  
            return {
                radius: data.backgroundRadius,
                color: {
                    color1: data.backgroundColor1,
                    color2: data.backgroundColor2,
                    angle: data.backgroundColorAngle
                },
                shadow: {
                    color: data.backgroundShadowColor,
                    horizontalOffset: data.backgroundShadowHorizontalOffset,
                    verticalOffset: data.backgroundShadowVerticalOffset,
                    blur: data.backgroundShadowBlur
                }
            }
        }

        if (data.backgroundType === rouletteItemBgType.IMAGE) {
            
            return {
                radius: data.backgroundRadius,
                image: {
                    url: data.backgroundImage?.url,
                    hueRotate: data.backgroundImageHueRotate,
                    saturate: data.backgroundImageSaturate
                },
                shadow: {
                    color: data.backgroundShadowColor,
                    horizontalOffset: data.backgroundShadowHorizontalOffset,
                    verticalOffset: data.backgroundShadowVerticalOffset,
                    blur: data.backgroundShadowBlur
                }
            }
        }

        return {};
    }, [rouletteData, rouletteStyle])

    const handleRouletteItemSettings = (item) => {
        console.log(item);
        setSettingsFormItem(item);
    }

    const handleRouletteItemHide = (value) => {
        console.log(value);
        const item = value.item;
        updateCategory({id: item.id, body: { id: item.id, hidden: value.hide }});
    }

    const handleOpenCreateReward = () => {
        setCreateCategoryForm(true);
        setSettingsFormItem(null);
    }

    useEffect(() => {
        if (isCategoryUpdated) {
            setSuccessfullyUpdatedCategory(isCategoryUpdated)
            const timeoutId = setTimeout(() => setSuccessfullyUpdatedCategory(false), 1500);

            return () => clearTimeout(timeoutId);
        }
    }, [isCategoryUpdated])

    useEffect(() => {
        if (isCategoryCreated) {
            setSuccessfullyCreatedCategory(isCategoryCreated)
            const timeoutId = setTimeout(() => setSuccessfullyCreatedCategory(false), 1500);

            return () => clearTimeout(timeoutId);
        }
    }, [isCategoryCreated])

    useEffect(() => {
        if (isCategoryDeleted) {
            setSuccessfullyDeletedCategory(isCategoryDeleted)
            const timeoutId = setTimeout(() => setSuccessfullyDeletedCategory(false), 1500);

            return () => clearTimeout(timeoutId);
        }
    }, [isCategoryDeleted])

    return (
        <Box>
            <Box>
                <RouletteComponent
                    ref={rouletteRef}
                    rouletteData={rouletteData}
                    spinDuration={5}
                    soundSrc={`${process.env.REACT_APP_CDN_BASE_URL}/sounds/tick.mp3`}
                    volume={0.2}
                    rouletteStyle={rouletteStyle}
                />
            </Box>
            <Box>
                <RouletteSettingsFormContext.Provider value={{ handleRouletteItemSettings, handleRouletteItemHide }}>
                    <Stack anchor="top-right">
                        <Tabs>
                            <Tab title={t('categoriesTitle')}>
                                <Box gap="medium">
                                    <Card>
                                        <CardBody gap="small" direction="row" justify="between" align="center">
                                            <Heading level={3} weight={'bold'} margin={'none'}>{t('rouletteRewards')}</Heading>
                                            <Box>
                                                <Button label={t('createRewardButton')} primary onClick={handleOpenCreateReward} />
                                            </Box>
                                        </CardBody>
                                    </Card>
                                    {(!settingsFormItem && !createCategoryForm) && isRouletteDataLoaded && <RouletteItems items={rouletteData.categoryEntries} />}
                                    {!createCategoryForm && settingsFormItem && <RouletteItemSettingsForm values={settingsFormItem} rouletteBackgroundStyle={rouletteBackground} onClose={() => setSettingsFormItem(null)} onSubmit={({ value }) => updateRouletteCategory({ id: value.id, body: value }).then((response) => setSettingsFormItem(null))} submitBusy={isUpdatingCategory} submitSuccess={isSuccessfullyUpdatedCategory} deleteBusy={isDelitingCategory} deleteSuccess={isSuccessfullyDeletedCategory} onDelete={(itemId) => deleteRouletteCategory({ id: itemId }).then((response) => setSettingsFormItem(null))} />}
                                    {!settingsFormItem && createCategoryForm && <RouletteItemSettingsForm rouletteBackgroundStyle={rouletteBackground} onClose={() => setCreateCategoryForm(false)} onSubmit={({ value }) => createRouletteCategory(value).then((response) => setCreateCategoryForm(false))} submitBusy={isCreatingCategory} submitSuccess={isSuccessfullyCreatedCategory} />}
                                </Box>
                            </Tab>
                            <Tab title={t('designTabTitle')}>
                                <RouletteSettingsForm values={rouletteData} />
                            </Tab>
                            <Tab title={t('themesTabTitle')}>
                                <RouletteThemesTab />
                            </Tab>
                        </Tabs>
                        <Box>
                            <Button label={t('spinButton')} onClick={() => rouletteRef.current.spin()} />
                        </Box>
                    </Stack>
                </RouletteSettingsFormContext.Provider>
            </Box>
        </Box>
    )
}

export default RouletteDesignSettings;